import React from "react";
import { Checkbox } from "../primitives/checkbox";
import { Label } from "../primitives/label";
import { cn } from "../utils";
import { SemoIcon as SemoIcon } from "@semo/icons";

export interface CheckCardProps {
  id?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: SemoIcon;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  footer?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function CheckCard({
  id,
  title,
  description,
  icon: Icon,
  checked,
  onCheckedChange,
  footer,
  className,
  disabled,
}: CheckCardProps) {
  const internalId = React.useId();
  const generatedId = id ?? internalId;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    // Prevent toggle if clicking directly on interactive elements
    const target = e.target;
    if (!(target instanceof Element)) return;

    const isControl =
      target.closest("button") ??
      target.closest("input") ??
      target.closest("label") ??
      target.closest("a");

    if (!isControl) {
      onCheckedChange(!checked);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-[12px] sm:rounded-[20px] border border-gray-100 bg-gray-50/30 transition-all duration-300",
        !disabled && "cursor-pointer group",
        !disabled &&
          !checked &&
          "hover:bg-white hover:border-primary/20 hover:shadow-sm",
        disabled && "opacity-50 cursor-not-allowed",
        checked &&
          "bg-white border-primary/30 ring-1 ring-primary/10 shadow-sm",
        className,
      )}
    >
      <div className="shrink-0 mt-0.5">
        <Checkbox
          id={generatedId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary transition-all rounded-[6px]"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <Label
            htmlFor={generatedId}
            className={cn(
              "text-sm sm:text-sm md:text-base font-semibold leading-tight text-gray-700 block tracking-tight transition-colors",
              !disabled && "cursor-pointer",
              !disabled && !checked && "group-hover:text-gray-900",
              checked && "text-primary",
            )}
          >
            {title}
          </Label>
          {Icon && (
            <Icon
              className={cn(
                "w-5 h-5 text-gray-300 shrink-0 transition-all duration-300",
                !disabled && "opacity-40",
                !disabled &&
                  !checked &&
                  "group-hover:opacity-100 group-hover:text-gray-400",
                checked && "text-primary opacity-100",
              )}
            />
          )}
        </div>
        {description && (
          <div
            className={cn(
              "text-2xs sm:text-xs text-gray-400 mt-1 leading-relaxed font-medium transition-colors",
              checked && "text-gray-500",
            )}
          >
            {description}
          </div>
        )}
        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </div>
  );
}
